import {
  useOrderListQuery,
  useOrdersExportMutation,
} from "@ticketingplatform/api/dist/order";
import { ColumnsType } from "antd/es/table";
import { useCallback, useMemo } from "react";
import { generatePath, Link } from "react-router-dom";
import { EyeOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { Order } from "@ticketingplatform/api/dist/order/fragments/__generated__/Order";
import { OrderWithRelations } from "@ticketingplatform/api/dist/order/fragments/__generated__/OrderWithRelations";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import { pageToSkip, renderTableDateZonedColumn } from "utils/tableUtils";
import {
  getColumnTextFilterProps,
  getTextFilterInput,
} from "components/data/TableTextFilter/utils";
import { useVenueContext } from "contexts/VenueContext";
import { ROUTE_PATHS } from "const/paths";
import { OrderListVariables } from "@ticketingplatform/api/dist/order/__generated__/OrderList";
import {
  OrderFilterInput,
  OrderStatus,
  TicketStatus,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import {
  getColumnEnumFilterProps,
  getEnumFilterValue,
} from "components/data/TableEnumFilter/utils";
import { useQueryParam } from "use-query-params";
import { useEventOccurrenceQuery } from "@ticketingplatform/api/dist/event";
import renderDateTimeZoned from "utils/renderDateTimeZoned";
import { Skeleton, message } from "antd";

import {
  UseEventOccurrenceFilterReturnType,
  UseExportOrdersReturnType,
  UseOrderListReturnType,
} from "./types";
import { DEFAULT_TABLE_PARAMS } from "./consts";

export const useOrderVariables = (
  andFilters?: OrderFilterInput
): OrderListVariables => {
  const { venue } = useVenueContext();
  const [{ page, pageSize, orderBy, filters }] =
    useTableQueryParams<Order>(DEFAULT_TABLE_PARAMS);

  return useMemo<OrderListVariables>(
    () => ({
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      filters: {
        ...(venue
          ? {
              tickets: {
                some: {
                  ticketPool: { event: { venueId: { equals: venue.id } } },
                },
              },
            }
          : {}),
        ...getTextFilterInput(filters?.firstName, "firstName"),
        ...getTextFilterInput(filters?.lastName, "lastName"),
        ...getTextFilterInput(filters?.orderNumber, "orderNumber"),
        ...getTextFilterInput(filters?.email, "email"),
        ...getEnumFilterValue(filters?.status, "status", OrderStatus),
        AND: andFilters ? [andFilters] : undefined,
      },
      orderBy,
      ticketsInclude: true,
      ticketsTake: 0,
      ticketsFilters: {
        status: {
          notIn: [TicketStatus.DELETED],
        },
      },
    }),
    [
      andFilters,
      filters?.email,
      filters?.firstName,
      filters?.lastName,
      filters?.orderNumber,
      filters?.status,
      orderBy,
      page,
      pageSize,
      venue,
    ]
  );
};

export const useOrderList = (
  filters?: OrderFilterInput,
  skip?: boolean
): UseOrderListReturnType => {
  const [{ page, pageSize }, setTableQueryParams] =
    useTableQueryParams<Order>(DEFAULT_TABLE_PARAMS);
  const variables = useOrderVariables(filters);
  const { data, loading } = useOrderListQuery({ variables, skip });

  const onChange = useCallback<UseOrderListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.orders || {};

  return useMemo(
    () => ({
      dataSource: dataSource || [],
      loading,
      onChange,
      pagination: {
        pageSize,
        current: page,
        total,
      },
    }),
    [dataSource, loading, onChange, page, pageSize, total]
  );
};

export const useOrderColumns = () => {
  const intl = useIntl();
  const [{ sortOrder, filters }] =
    useTableQueryParams<OrderWithRelations>(DEFAULT_TABLE_PARAMS);

  const columns: ColumnsType<OrderWithRelations> = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "First Name",
        }),
        key: "firstName",
        dataIndex: "firstName",
        sorter: {
          multiple: 1,
        },
        sortOrder: sortOrder.firstName,
        ...getColumnTextFilterProps("firstName", filters?.firstName),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Last Name",
        }),
        key: "lastName",
        dataIndex: "lastName",
        sorter: {
          multiple: 2,
        },
        sortOrder: sortOrder.lastName,
        ...getColumnTextFilterProps("lastName", filters?.lastName),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Order Number",
        }),
        key: "orderNumber",
        dataIndex: "orderNumber",
        sorter: {
          multiple: 3,
        },
        sortOrder: sortOrder.orderNumber,
        ...getColumnTextFilterProps("orderNumber", filters?.orderNumber),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Email",
        }),
        key: "email",
        dataIndex: "email",
        sorter: {
          multiple: 4,
        },
        sortOrder: sortOrder.email,
        ...getColumnTextFilterProps("email", filters?.email),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Tickets",
        }),
        key: "tickets",
        render: (_, { tickets }) => tickets?.count || 0,
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Order Date",
        }),
        key: "createdAt",
        dataIndex: "createdAt",
        sorter: {
          multiple: 5,
        },
        sortOrder: sortOrder.createdAt,
        render: renderTableDateZonedColumn("createdAt"),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Status",
        }),
        key: "status",
        dataIndex: "status",
        sorter: {
          multiple: 6,
        },
        sortOrder: sortOrder.status,
        ...getColumnEnumFilterProps(
          "status",
          filters?.status,
          false,
          OrderStatus
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Actions",
        }),
        key: "action",
        render: (_, { id }) => (
          <Link
            to={generatePath(ROUTE_PATHS.OrderRead, {
              id: id.toString(),
            })}
          >
            <EyeOutlined />
          </Link>
        ),
      },
    ],
    [
      intl,
      sortOrder.firstName,
      sortOrder.lastName,
      sortOrder.orderNumber,
      sortOrder.email,
      sortOrder.createdAt,
      sortOrder.status,
      filters?.firstName,
      filters?.lastName,
      filters?.orderNumber,
      filters?.email,
      filters?.status,
    ]
  );

  return { columns };
};

export const useEventOccurrenceFilter =
  (): UseEventOccurrenceFilterReturnType => {
    const intl = useIntl();
    const [eventOccurrenceId, setEventOccurrenceId] = useQueryParam<
      string | undefined
    >("eventOccurrenceId");

    const { data, loading } = useEventOccurrenceQuery(
      eventOccurrenceId
        ? {
            variables: {
              id: eventOccurrenceId,
              eventInclude: true,
            },
          }
        : { skip: true }
    );

    const { eventOccurrence } = data || {};

    return useMemo<UseEventOccurrenceFilterReturnType>(() => {
      if (!eventOccurrenceId) {
        return {
          isValid: true,
        };
      }

      const baseAlert: UseEventOccurrenceFilterReturnType["alert"] = {
        type: "info",
        closable: true,
        style: {
          marginBottom: "1rem",
        },
        onClose: () => setEventOccurrenceId(undefined),
      };

      if (loading) {
        return {
          isValid: false,
          alert: {
            ...baseAlert,
            message: <Skeleton paragraph={false} />,
          },
        };
      }

      if (!eventOccurrence) {
        return {
          isValid: false,
          alert: {
            ...baseAlert,
            type: "error",
            message: intl.formatMessage({
              defaultMessage: "Event Not Found",
            }),
          },
        };
      }

      const { date, event } = eventOccurrence;

      return {
        isValid: true,
        alert: {
          ...baseAlert,
          message: `${event.name}, ${renderDateTimeZoned(
            date,
            event.timezone
          )}`,
        },
        filters: {
          tickets: {
            some: {
              ticketPool: {
                eventId: {
                  equals: event.id,
                },
              },
            },
          },
        },
      };
    }, [
      eventOccurrence,
      eventOccurrenceId,
      intl,
      loading,
      setEventOccurrenceId,
    ]);
  };

export const useExportOrders = (
  andFilters?: OrderFilterInput
): UseExportOrdersReturnType => {
  const intl = useIntl();
  const { filters, orderBy } = useOrderVariables(andFilters);

  const [ordersExportMutation, { loading }] = useOrdersExportMutation({
    variables: {
      filters,
      orderBy,
    },
    onError() {
      message.error(
        intl.formatMessage({
          defaultMessage: "Failed to export orders",
        })
      );
    },
  });

  return useMemo(
    () => ({
      loading,
      async exportOrders() {
        const { data } = await ordersExportMutation();
        const { url } = data?.ordersExport || {};

        if (url) {
          window.open(url);
        }
      },
    }),
    [loading, ordersExportMutation]
  );
};
